'use client';
import { FormikHelpers, FormikErrors, FormikContextType } from 'formik';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { STRIPE_ELEMENT_OPTIONS } from '@/const/stripe';
import { useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';

import './SignupStep4Form.style.scss';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import AddressInputField from '@/components/form/lib/InputField/AddressInputField';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import Select from '@/components/form/lib/InputField/Select';

import CheckBox from '@/components/common/CheckBox/CheckBox';
import Text from '@/components/common/Text/Text';
import StripeElement from '@/components/common/StripeElement/StripeElement';

import { US_STATES } from '@/util/user_client';
import { PaymentToken } from '@/types/Payment';

export type SignupStep4FormValues = {
  creditCardLast4: string | undefined;
  expirationDate: string;
  sameAsShippingAddress: boolean;
  stripePaymentMethodTokenId: string;
  stripePaymentMethodBrand: string;
  billingAddress: string;
  billingAptUnitSuite?: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
};

interface SignupStep4FormProps
  extends Omit<BaseFormProps<SignupStep4FormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: SignupStep4FormValues,
    actions: FormikHelpers<SignupStep4FormValues>
  ) => void;
}

export default function SignupStep4Form(props: SignupStep4FormProps) {
  const {
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = false
  } = props;
  const elements = useElements();
  const stripe = useStripe();

  const [cardNumberError, setCardNumberError] = useState<string | null>();
  const [cardExpiryError, setCardExpiryError] = useState<string | null>();
  const [cardCvcError, setCardCvcError] = useState<string | null>();
  const [generalError, setGeneralError] = useState<string | null>();
  const [isSameAsShippingAddress, setIsSameAsShippingAddress] = useState(
    initialValues.sameAsShippingAddress
  );

  function defaultValidation(
    values: SignupStep4FormValues
  ): FormikErrors<SignupStep4FormValues> {
    const { billingAddress, billingCity, billingPostalCode, billingState } =
      values;
    const errors: FormikErrors<SignupStep4FormValues> = {};

    if (!isSameAsShippingAddress) {
      if (!billingAddress) {
        errors.billingAddress = 'Please enter a valid address';
      }

      if (!billingCity) {
        errors.billingCity = 'Please enter a valid city';
      }

      if (!billingState) {
        errors.billingState = 'Please enter a valid state';
      }

      if (!billingPostalCode || billingPostalCode.length !== 5) {
        errors.billingPostalCode = 'Please enter a valid postal code';
      }
    }

    return errors;
  }

  const handleStripeElementsChange = (e: any) => {
    // Set error message to be shown when the user inputs incorrect payment data
    const cardNumberType = e.elementType === 'cardNumber';
    const cardExpiryType = e.elementType === 'cardExpiry';
    const cardCvcType = e.elementType === 'cardCvc';

    if (cardNumberType && e.empty) {
      setCardNumberError('A valid credit card number is required');
    } else if (cardNumberType && typeof e.error === 'object') {
      setCardNumberError(e.error.message);
    } else if (cardNumberType && typeof e.error !== 'object') {
      setCardNumberError('');
    }

    if (cardExpiryType && e.empty) {
      setCardExpiryError('A valid expiration date is required');
    } else if (cardExpiryType && typeof e.error === 'object') {
      setCardExpiryError(e.error.message);
    } else if (cardExpiryType && typeof e.error !== 'object') {
      setCardExpiryError('');
    }

    if (cardCvcType && e.empty) {
      setCardCvcError('CVC is required');
    } else if (cardCvcType && typeof e.error === 'object') {
      setCardCvcError(e.error.message);
    } else if (cardCvcType && typeof e.error !== 'object') {
      setCardCvcError('');
    }
  };

  function handleBillingAddress() {
    setIsSameAsShippingAddress(!isSameAsShippingAddress);
  }

  const handleSubmit = async (values: SignupStep4FormValues) => {
    const card = elements?.getElement('cardNumber') as StripeCardNumberElement;
    let paymentMethod: PaymentToken;

    if (!card) {
      setGeneralError('There was an issue adding your payment method');
      onSubmitFailure?.(values);
      return;
    }

    try {
      paymentMethod = (await stripe?.createToken(card)) as PaymentToken;
    } catch (error) {
      setGeneralError('Please check your credit card information');
      onSubmitFailure?.(values);
      return;
    }

    if (paymentMethod.error) {
      // We're letting Stripe handle validation errors for us here on submit
      return;
    }

    onSubmitSuccess?.({
      ...values,
      creditCardLast4: paymentMethod?.token.card?.last4,
      expirationDate: `${paymentMethod?.token.card?.exp_month}/${paymentMethod?.token.card?.exp_year}`,
      stripePaymentMethodTokenId: paymentMethod?.token.id,
      stripePaymentMethodBrand: paymentMethod?.token.card?.brand,
      sameAsShippingAddress: isSameAsShippingAddress
    });
  };

  function onAddressSelected(
    formikContext: FormikContextType<unknown>,
    formattedAddress: string
  ) {
    const { billingAddress, billingCity, billingState, billingPostalCode } =
      parseAddress(formattedAddress);

    formikContext.setFieldValue('billingAddress', billingAddress, false);
    formikContext.setFieldValue('billingCity', billingCity, false);
    formikContext.setFieldValue('billingState', billingState, false);
    formikContext.setFieldValue('billingPostalCode', billingPostalCode, false);
  }

  function parseAddress(
    billingAddress: string
  ): Partial<SignupStep4FormValues> {
    const addressArray = billingAddress.split(',');

    const fullBillingAddress: Partial<SignupStep4FormValues> = {
      billingAddress: addressArray[0] || '',
      billingCity: addressArray[1]?.trim() || '',
      billingState: addressArray[2]?.trim().split(' ')[0] || '',
      billingPostalCode: addressArray[2]?.trim().split(' ')[1] || ''
    };

    return fullBillingAddress;
  }

  return (
    <div className='SignupStep4Form'>
      <h1 className='Signup-step-title'>Complete your application</h1>
      <div className='Signup-progress-bar'>
        <div
          className='Progress-bar-fill'
          style={{ width: '80%' }}
        ></div>
      </div>
      <h3 className='Signup-step-subtitle'>Billing</h3>

      <Form<SignupStep4FormValues>
        initialValues={initialValues}
        validate={validate}
        // If an 'onSubmit function' is passed in, that function will override
        // this component's 'handleSubmit' function.
        // @ts-ignore
        onSubmit={onSubmit || handleSubmit}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        <StripeElement
          error={cardNumberError}
          label='Credit card number'
        >
          <CardNumberElement
            id='cardNumber'
            options={STRIPE_ELEMENT_OPTIONS}
            onChange={handleStripeElementsChange}
          />
        </StripeElement>

        <div className='Signup-fields-wrapper'>
          <StripeElement
            error={cardExpiryError}
            label={'expiration date'}
          >
            <CardExpiryElement
              options={STRIPE_ELEMENT_OPTIONS}
              onChange={handleStripeElementsChange}
            />
          </StripeElement>
          <StripeElement
            error={cardCvcError}
            label={'CVC'}
          >
            <CardCvcElement
              options={STRIPE_ELEMENT_OPTIONS}
              onChange={handleStripeElementsChange}
            />
          </StripeElement>
        </div>

        <div className='InputField-checkbox'>
          <CheckBox
            checked={isSameAsShippingAddress}
            label={'My billing address is the same as my shipping address'}
            onChange={handleBillingAddress}
          />
        </div>

        {!isSameAsShippingAddress && (
          <div className='Signup-billing-address'>
            <h3 className='Signup-step-subtitle'>Billing Address</h3>
            <AddressInputField
              className='Signup-address'
              label={'Address'}
              onAddressSelected={(formikContext, formattedAddress) =>
                onAddressSelected(formikContext, formattedAddress)
              }
              name='billingAddress'
              defaultValue={initialValues.billingAddress}
            />
            <div className='Signup-fields-wrapper'>
              <InputField
                className='Signup-apt-unit-suite'
                name='billingAptUnitSuite'
                label={'apt/unit/suite (optional)'}
                type='text'
              />
              <InputField
                className='Signup-city'
                name='billingCity'
                label={'City'}
                type='text'
              />
              <Select
                name='billingState'
                label={'State'}
                options={US_STATES}
              />
              <MaskedField
                id='billingPostalCode'
                className='Signup-postal-code'
                guide={false}
                mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                label={'Postal Code'}
                name='billingPostalCode'
              />
            </div>
          </div>
        )}

        <button
          className='Signup-Continue-Button cursor'
          type='submit'
        >
          Continue
        </button>

        {generalError && (
          <Text className='UpdateContactForm-error'>{generalError}</Text>
        )}
      </Form>
    </div>
  );
}
